import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";
import { ENTER } from "@/lib/motion";

/**
 * 아무것도 없을 때 그 자리를 채우는 것.
 *
 * 화면 네 곳이 같은 생김새를 쓴다 — 대화를 고르기 전, 없는 주소, 못 찾은 방,
 * 서버를 못 부를 때. 같은 디자인이면 같은 컴포넌트다(react-component-rules §1).
 *
 * 빈 화면은 "여기 아무것도 없다"로 끝나면 안 된다. 다음에 무엇을 할 수 있는지
 * 같이 줘야 사용자가 멈추지 않는다. 그래서 `actions`가 있다.
 */
const tile = cva("flex shrink-0 items-center justify-center bg-empty text-muted", {
  variants: {
    size: {
      sm: "size-14 rounded-[18px] [&_svg]:size-6",
      /** 처음 온 사람에게 이 앱을 소개할 때처럼, 한 화면을 통째로 쓸 때 */
      md: "size-18 rounded-[22px] [&_svg]:size-7",
    },
  },
  defaultVariants: { size: "sm" },
});

type EmptyStateProps = VariantProps<typeof tile> & {
  icon: ReactNode;
  title: string;
  /** 한두 줄. 왜 비어 있는지, 또는 무엇을 하면 되는지 */
  description?: string;
  /** 버튼 한두 개. 없으면 안 그린다 */
  actions?: ReactNode;
  className?: string;
};

export function EmptyState({
  size,
  icon,
  title,
  description,
  actions,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        `flex flex-1 flex-col items-center justify-center gap-3.5 px-8 text-center ${ENTER}`,
        className,
      )}
    >
      <span className={tile({ size })}>{icon}</span>

      <div className="flex flex-col gap-1.5">
        <p className="text-[15px] font-semibold text-ink">{title}</p>
        {description ? (
          <p className="max-w-84 text-[13px] leading-[1.6] text-muted">{description}</p>
        ) : null}
      </div>

      {actions ? <div className="mt-1 flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}
